import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import URL from 'url-parse';
import queryString from 'query-string';
import { __RouterContext } from 'react-router';

import Styled from 'styled';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import scalarQuery from 'lib/react-router/scalar-query';

import arrowLeft from 'four-nets/icons/svg/icon_arrow-left.svg';
import arrowRight from 'four-nets/icons/svg/icon_arrow-right.svg';

import PagerElementView from './pager-element-view';
import defaultStyles from './pager-view.scss';


type PagerElementProps = {
    onClick?: () => void,
    content: string | number,
    selected?: boolean,
};

@Styled(defaultStyles)
@observer
class PagerView extends React.Component {
    static contextType = __RouterContext;

    static defaultProps = {
        query: query => query,
        linkify: true,
    };

    onPage = page => event => {
        if (this.props.onPage) {
            this.props.onPage(event, page);
        }
    };

    render() {
        const { router } = this.context;
        const { current, count, query, styles, linkify } = this.props;

        if (!count || count < 2) {
            return null;
        }

        const { location } = this.context;
        let { pathname } = location;

        if (!pathname.endsWith('/')) {
            pathname += '/';
        }

        const url = new URL(pathname, {});
        const { page: _, ...rest } = scalarQuery(queryString.parse(location.search));
        url.set('query', query(rest));

        let page;

        const elements: PagerElementProps[] = [];

        if (current > 1) {
            page = current - 1;
            url.set('query', { ...url.query, page });

            elements.push({
                content: <SVGIcon style={{image: styles.icon}} src={arrowLeft} />,
                onClick: this.onPage(page),
                href: url.toString(queryString.stringify),
            });

            url.set('query', { ...url.query, page: undefined });
            elements.push({
                content: '1',
                onClick: this.onPage(1),
                href: url.toString(queryString.stringify),
            });

            if (current > 3) {
                elements.push({
                    content: '...',
                });
            }
        }

        if (current > 2) {
            page = current - 1;
            url.set('query', {
                ...url.query,
                page,
            });

            elements.push({
                content: `${page}`,
                onClick: this.onPage(page),
                href: url.toString(queryString.stringify),
            });
        }

        elements.push({
            content: `${current}`,
            selected: true,
        });

        if (count > current + 1) {
            page = current + 1;
            url.set('query', {
                ...url.query,
                page,
            });

            elements.push({
                content: `${page}`,
                onClick: this.onPage(page),
                href: url.toString(queryString.stringify),
            });
        }

        if (count > current) {
            if (current < count - 2) {
                elements.push({
                    content: '...',
                });
            }

            page = count;
            url.set('query', {
                ...url.query,
                page,
            });

            elements.push({
                content: `${count}`,
                onClick: this.onPage(count),
                href: url.toString(queryString.stringify),
            });

            page = current + 1;
            url.set('query', {
                ...url.query,
                page,
            });

            elements.push({
                content: <SVGIcon style={{image: styles.icon}} src={arrowRight} />,
                onClick: this.onPage(page),
                href: url.toString(queryString.stringify),
            });
        }

        return (
            <nav className={styles.pagerView}>
                <ul className={styles.elements}>
                    {elements.map(({ content, ...props }, index) => (
                        <li className={styles.element} key={index.toString()}>
                            <PagerElementView {...props} linkify={linkify}>
                                {content}
                            </PagerElementView>
                        </li>
                    ))}
                </ul>
            </nav>
        );
    }
}

export default PagerView;
